import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { CheckCircle, ExternalLink } from 'lucide-react';
import { Button, Tooltip } from '@getro/rombo';
import { Rocket } from './rocket';
import { FormBuilderDrawer } from './formBuilder';
import { useEnabledState } from './hooks/useEnabledState';
import { Stepper } from '../../../components/atoms/Stepper';
import { GrowListAgentModal } from '../aiAgent/growListAgent';

const HOW_IT_WORKS_STEPS = [
  'You define who you want to reach',
  'The agent finds relevant people based on your criteria',
  'The agent sends invitations to join this list',
];

export const EnabledState = ({ sharedList, formConfig, listAgentsEnabled }) => {
  const {
    showFormBuilder,
    showSuccessEditor,
    copied,
    isDisabling,
    signupFormUrl,
    handleCopyLink,
    handleDisableSignupForm,
    handleSetupAgent,
    openFormBuilder,
    closeFormBuilder,
    openSuccessEditor,
    closeSuccessEditor,
    isGrowListAgentOpen,
    agentCampaign,
    handleCloseGrowListAgent,
    isCreatingCampaign,
    hasAgent,
  } = useEnabledState({ sharedList, formConfig });

  return (
    <>
      <Flex
        sx={{
          flexDirection: 'column',
          height: '100%',
          px: '16px',
          py: '48px',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '40px',
            maxWidth: '800px',
            width: '100%',
            margin: '0px auto',
          }}
        >
          <Flex
            sx={{
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Flex sx={{ alignItems: 'center', gap: '8px' }}>
              <Text
                sx={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'text.dark',
                }}
              >
                Signup form is live
              </Text>
              <Box as={CheckCircle} width="16px" height="16px" aria-hidden="true" sx={{ color: 'green.600' }} />
            </Flex>
            <Text sx={{ fontSize: '14px', color: 'text.main' }}>
              Share this link to invite people to join this list.
            </Text>
            <Flex
              sx={{
                alignItems: 'center',
                gap: '8px',
                p: '8px 16px',
                borderRadius: '4px',
                border: '1px solid',
                borderColor: 'border.default',
                height: '48px',
              }}
            >
              <Text
                as="a"
                href={signupFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flex: 1,
                  fontSize: '14px',
                  color: 'text.main',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                }}
              >
                {signupFormUrl}
              </Text>
              <Box
                as="a"
                href={signupFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.main',
                  '&:hover': { color: 'purple.400' },
                }}
              >
                <Box as={ExternalLink} width="16px" height="16px" aria-hidden="true" />
              </Box>
              <Tooltip
                position="top"
                content={<Text fontSize="12px">{copied ? 'Copied!' : 'Copy link'}</Text>}
                trigger={
                  <Button variant="accent" size="small" onClick={handleCopyLink}>
                    Copy link
                  </Button>
                }
              />
            </Flex>
          </Flex>

          <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
            <Text sx={{ fontSize: '16px', fontWeight: '600', color: 'text.dark' }}>Form customization</Text>
            <Text sx={{ fontSize: '14px', color: 'text.main', mb: '8px' }}>
              Adjust the fields and messaging people see when signing up.
            </Text>
            <Flex sx={{ gap: '8px' }}>
              <Button variant="secondary" size="small" onClick={openFormBuilder}>
                Edit form
              </Button>
              <Button variant="secondary" size="small" onClick={openSuccessEditor}>
                Edit success message
              </Button>
            </Flex>
          </Flex>

          {listAgentsEnabled && (
            <Box>
              <Box
                sx={{
                  borderRadius: '4px',
                  border: '1px solid transparent',
                  backgroundImage:
                    'linear-gradient(white, white), linear-gradient(94deg, #4900FF 3.21%, #D400FF 63.82%, #FF9257 134.26%)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                }}
              >
                <Flex
                  sx={{
                    borderRadius: '4px',
                    gap: '16px',
                    p: '16px',
                    background:
                      'linear-gradient(251deg, rgba(255, 159, 138, 0.08) -5.07%, rgba(255, 68, 255, 0.08) 65.71%, rgba(73, 0, 255, 0.08) 107.1%), #FFF',
                  }}
                >
                  <Rocket />

                  <Flex
                    sx={{
                      flexDirection: 'column',
                      gap: '16px',
                    }}
                  >
                    <Flex sx={{ alignItems: 'flex-start', gap: '12px' }}>
                      <Flex sx={{ flexDirection: 'column', gap: '4px', flex: 1 }}>
                        <Text sx={{ fontSize: '14px', fontWeight: '600', color: 'text.dark' }}>
                          Automatically grow this list
                        </Text>
                        <Text sx={{ fontSize: '14px', color: 'text.main', lineHeight: '20px' }}>
                          Launch an agent that finds relevant people outside your network and invites them to join this
                          list, so you&apos;re ready when the need comes.
                        </Text>
                      </Flex>
                    </Flex>

                    {/* How it works */}
                    <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
                      <Text
                        sx={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: 'purple.500',
                          textTransform: 'uppercase',
                        }}
                      >
                        How it works
                      </Text>
                      <Stepper steps={HOW_IT_WORKS_STEPS} />
                      <Button
                        variant="primary"
                        size="small"
                        sx={{ alignSelf: 'flex-start', mt: '4px' }}
                        onClick={handleSetupAgent}
                        loading={isCreatingCampaign}
                        disabled={isCreatingCampaign}
                      >
                        {hasAgent && <Box as={ExternalLink} width="16px" height="16px" mr="8px" aria-hidden="true" />}
                        {hasAgent ? 'Open agent' : 'Set up agent'}
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              </Box>
            </Box>
          )}
          <Flex
            sx={{
              flexDirection: 'column',
              gap: '8px',
              pt: '16px',
              borderTop: '1px solid',
              borderColor: 'border.subtle',
            }}
          >
            <Text sx={{ fontSize: '16px', fontWeight: '600', color: 'text.dark' }}>Disable signup form</Text>
            <Text sx={{ fontSize: '14px', color: 'text.main', mb: '8px' }}>
              If you no longer want to use this form, disable it. Signed up profiles and form settings will be kept, so
              you can re-enable it anytime.
            </Text>
            <Button
              variant="secondary"
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              onClick={handleDisableSignupForm}
              loading={isDisabling}
              disabled={isDisabling}
            >
              Disable signup form
            </Button>
          </Flex>
        </Flex>
      </Flex>

      {showFormBuilder && (
        <FormBuilderDrawer
          collectionId={sharedList.collection?.id}
          sharedListId={sharedList.id}
          onClose={closeFormBuilder}
          networkName={sharedList.collection?.name || 'Your Company'}
          type="form"
          networkLogo={sharedList.collection?.logoUrl}
        />
      )}

      {showSuccessEditor && (
        <FormBuilderDrawer
          collectionId={sharedList.collection?.id}
          sharedListId={sharedList.id}
          onClose={closeSuccessEditor}
          networkName={sharedList.collection?.name || 'Your Company'}
          networkLogo={sharedList.collection?.logoUrl}
          type="success"
        />
      )}

      {listAgentsEnabled && isGrowListAgentOpen && (
        <GrowListAgentModal
          isOpen={isGrowListAgentOpen}
          onClose={handleCloseGrowListAgent}
          sharedList={sharedList}
          campaign={agentCampaign}
        />
      )}
    </>
  );
};

EnabledState.propTypes = {
  sharedList: PropTypes.shape({
    id: PropTypes.number,
    slug: PropTypes.string,
    collection: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      logoUrl: PropTypes.string,
    }),
  }).isRequired,
  formConfig: PropTypes.shape({
    id: PropTypes.number,
    active: PropTypes.bool,
  }).isRequired,
  listAgentsEnabled: PropTypes.bool,
};

EnabledState.defaultProps = {
  listAgentsEnabled: false,
};
